import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaStar,
  FaExternalLinkAlt,
  FaGlobe,
  FaShare,
} from "react-icons/fa";
import "./VenueDetail.css";
import LoadingPage from "../../components/Loading/LoadingPage";
import {
  saveCouponToWishlist,
  removeSavedCoupon,
  getCustomerWishlist,
  isCouponInWishlist,
} from "../../services/wishlistService";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useToast } from "../../hooks/useToast";
import { useVenueInterest } from "../../hooks/useVenueInterest";
import { appendCompanyCode } from "../../hooks/useCompanyCode";
import VenueCouponsSection from "../../components/VenueCouponsSection/VenueCouponsSection";
import DetailPageLayout from "../../components/DetailPageLayout/DetailPageLayout";
import VenueInfoCard from "./VenueInfoCard";
import bcLogo from "../../media/BC.png";

export default function VenueDetail() {
  const { customerToken: tokenFromParams, venueId } = useParams();
  const navigate = useNavigate();
  const { viewMode, customerToken: tokenFromAuth, triggerLogin, setShowLoading, updateWishlistCount } = useAuth();

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);
  const [venueDetails, setVenueDetails] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [error, setError] = useState("");

  const headers = useMemo(() => ({ "Content-Type": "application/json" }), []);

  // Wishlist state
  const [wishlistCoupons, setWishlistCoupons] = useState([]);
  const [savingCouponId, setSavingCouponId] = useState(null);
  const [venueHasActiveOffers, setVenueHasActiveOffers] = useState(null);

  // Tracking coupon visibility for go-to-coupon button
  const [couponsSectionVisible, setCouponsSectionVisible] = useState(true);
  const couponsRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(false);

  // Use token from auth context if available, otherwise from URL params
  const customerToken = tokenFromAuth || tokenFromParams;
  const isPublicMode = viewMode === "public" || customerToken === "public";
  const isMobile = useIsMobile();

  // Toast notifications
  const { toast, showToast } = useToast();

  // Venue interest management
  const { isInterested, interestedLoading, handleInterestToggle } = useVenueInterest({
    venueId,
    customerToken,
    isPublicMode,
    triggerLogin,
    showToast,
    apiUrl,
  });

  // Handle auth changes and refresh flags
  React.useEffect(() => {
    const shouldForceRefresh = sessionStorage.getItem("force_auth_refresh");
    const userJustLoggedIn = tokenFromAuth && tokenFromAuth !== "public" && tokenFromParams === "public";

    if (shouldForceRefresh || userJustLoggedIn) {
      if (shouldForceRefresh) {
        sessionStorage.removeItem("force_auth_refresh");
        window.dispatchEvent(new Event('focus'));
      }
      setContentReady(false);
      setLoading(true);
    }
  }, [tokenFromAuth, tokenFromParams]);

  // Control header/footer visibility when showing loading page
  useEffect(() => {
    setShowLoading(!contentReady);
  }, [contentReady, setShowLoading]);

  // Set content ready after loading completes to ensure all state updates are processed
  useEffect(() => {
    if (!loading && !contentReady) {
      requestAnimationFrame(() => {
        setContentReady(true);
      });
    }
  }, [loading, contentReady]);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        setLoading(true);
        setContentReady(false); // Hide content while loading

        // Require both customerToken and venueId
        if (!customerToken || !venueId) {
          setError("Missing required parameters.");
          setLoading(false);
          return;
        }

        // Fetch venue details (works for both public and authenticated modes)
        const venueDetailsResponse = await fetch(
          `${apiUrl}/coupon/venue/${venueId}/details`,
          { headers }
        );
        if (venueDetailsResponse.ok) {
          const venueData = await venueDetailsResponse.json();
          if (ok) {
            setVenueDetails(venueData);
          }
        }

        // PUBLIC MODE: Use public endpoint to fetch redeemable coupons
        if (isPublicMode) {
          const publicEndpoint = `${apiUrl}/coupon/venue/${venueId}/redeemable-coupons`;
          const response = await fetch(publicEndpoint, { headers });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || "Failed to fetch coupons");
          }

          const data = await response.json();
          if (!ok) return;

          // Backend returns coupons in the same format as customer endpoint
          // All coupons are already filtered to be non-expired and active
          setCoupons(Array.isArray(data.coupons) ? data.coupons : []);
          setWishlistCoupons([]); // No wishlist for public users
          // Explicitly set to boolean, not undefined
          setVenueHasActiveOffers(data.venue_has_active_offers === true);
          setError(""); // Clear any errors
          return;
        }

        // AUTHENTICATED MODE: Use customer endpoint
        const couponEndpoint = venueId
          ? `${apiUrl}/coupon/customer/${encodeURIComponent(
            customerToken
          )}/venue/${venueId}`
          : `${apiUrl}/coupon/customer/${encodeURIComponent(customerToken)}`;

        // Fetch coupons + wishlist in parallel
        const [couponRes, wishlistData] = await Promise.all([
          fetch(couponEndpoint, { headers }),
          getCustomerWishlist(customerToken).catch(() => ({
            saved_coupons: [],
          })),
        ]);

        // Handle coupons response
        if (!couponRes.ok) {
          const errorData = await couponRes.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to fetch coupons");
        }

        const data = await couponRes.json();
        if (!ok) return;

        // Set coupons and saved list (so isSaved is correct on first paint)
        setCoupons(Array.isArray(data.coupons) ? data.coupons : []);
        setWishlistCoupons(wishlistData?.saved_coupons || []);
        // Explicitly set to boolean, not undefined
        setVenueHasActiveOffers(data.venue_has_active_offers === true);
        setError(""); // Clear any errors
      } catch (err) {
        console.error("Error fetching coupons:", err);
        setError(err.message || "Failed to load coupons. Please try again.");
        setCoupons([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [apiUrl, headers, customerToken, venueId, isPublicMode]);

  const handleWishlistToggle = React.useCallback(async (coupon) => {
    // Prevent wishlist actions in public mode - trigger login modal
    if (isPublicMode) {
      triggerLogin();
      return;
    }
    if (!customerToken) {
      alert("Please sign in to save coupons to your wishlist");
      return;
    }
    const isActive =
      coupon.status === "ready" && new Date(coupon.expires_at) > new Date();
    if (!isActive) return;

    const savedCoupon = isCouponInWishlist(wishlistCoupons, coupon.coupon_id);
    try {
      setSavingCouponId(coupon.coupon_id);
      if (savedCoupon) {
        await removeSavedCoupon(savedCoupon.saved_id, customerToken);
        const updatedWishlist = wishlistCoupons.filter((item) => item.coupon_id !== coupon.coupon_id);
        setWishlistCoupons(updatedWishlist);
        updateWishlistCount(updatedWishlist.length);
        showToast("Removed from wishlist", "info");
      } else {
        const result = await saveCouponToWishlist(
          customerToken,
          coupon.coupon_id,
          venueId || coupon.venue_id
        );
        const updatedWishlist = [
          ...wishlistCoupons,
          {
            saved_id: result.saved_id,
            coupon_id: result.coupon_id,
            venue_id: result.venue_id,
            saved_at: result.saved_at,
          },
        ];
        setWishlistCoupons(updatedWishlist);
        updateWishlistCount(updatedWishlist.length);
        showToast("Saved to wishlist!", "success");
      }
    } catch (err) {
      console.error("Wishlist toggle error:", err);
      showToast(err.message || "Failed to update wishlist", "error");
    } finally {
      setSavingCouponId(null);
    }
  });

  // Detect when coupons section leaves viewport
  useEffect(() => {
    // Wait for contentReady so the ref is actually attached to DOM
    if (!contentReady || !couponsRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setCouponsSectionVisible(entry.isIntersecting);
      },
      {
        threshold: 0.3,
        rootMargin: '-35% 0px -35% 0px',
      }
    );

    observer.observe(couponsRef.current);

    return () => observer.disconnect();
  }, [contentReady]);

  // Detect when user scrolls to bottom of page to hide the popup bar
  useEffect(() => {
    if (!isMobile || !contentReady) return;

    const handleScroll = () => {
      // Use clientHeight (stable) instead of innerHeight (changes as browser chrome shows/hides on mobile scroll)
      const windowHeight = document.documentElement.clientHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      // Consider "at bottom" if within 100px of the bottom
      const threshold = 100;
      const atBottom = windowHeight + scrollTop >= documentHeight - threshold;

      setIsAtBottom(atBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, contentReady]);

  // Show loading page (will render outside of normal page layout to cover everything)
  if (!contentReady) {
    return (
      <div tyle={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: '#FFF9F5'
      }}>
        <LoadingPage />
      </div>
    );
  }

  // Calculate ready coupons for the floating button
  const now = new Date();
  const readyCoupons = isPublicMode
    ? coupons
    : coupons.filter(
      (c) => c.status === "ready" && new Date(c.expires_at) > now
    );

  // Helper function to open Google Maps
  const openInGoogleMaps = () => {
    if (!venueDetails?.full_address) return;
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      venueDetails.full_address
    )}`;
    window.open(mapsUrl, "_blank", "noopener,noreferrer");
  };

  // Share venue function
  const handleShareVenue = async () => {
    if (!venueDetails) return;

    // Create public URL using current origin and public path
    const origin = window.location.origin;
    const shareUrl = `${origin}/coupons/public/venue/${venueId}`;

    const couponText = readyCoupons.length > 0
      ? `${readyCoupons.length} exclusive ${readyCoupons.length === 1 ? 'deal' : 'deals'} available!`
      : 'Check out this venue!';

    const shareData = {
      title: venueDetails.venue_name,
      text: `${venueDetails.venue_name} - ${couponText} ${venueDetails.description ? venueDetails.description.slice(0, 100) : ''}`,
      url: shareUrl,
    };

    // Try Web Share API first (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        showToast("Shared successfully!", "success");
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Fallback: Copy link to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast("Link copied to clipboard!", "success");
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        showToast("Could not copy link", "error");
      }
    }
  };

  // Hero overlay content
  const heroOverlay = venueDetails && (
    <div className="venue-hero-content-inner">
      {/* Category badge */}
      {venueDetails.neighborhood && (
        <div className="venue-category-badges">
          <span className="category-badge neighborhood-badge">
            <FaMapMarkerAlt />
            {venueDetails.neighborhood}
          </span>
        </div>
      )}

      {/* Venue Title - Now overlays image */}
      <h1 className="venue-name-hero">{venueDetails.venue_name}</h1>

      {/* Meta badges - Rating and Price */}
      <div className="venue-meta-row">
        {venueDetails.rating && (
          <span className="meta-badge rating-badge">
            <FaStar />
            <span className="rating-value">{venueDetails.rating.toFixed(1)}</span>
            {venueDetails.rating_count > 0 && (
              <span className="rating-count">({venueDetails.rating_count})</span>
            )}
          </span>
        )}
        {venueDetails.price_text && (
          <span className="meta-badge price-badge">
            💰 {venueDetails.price_text}
          </span>
        )}
      </div>
    </div>
  );

  // Left column content
  const leftColumn = (
    <>
      {/* VENUE INFO CARD - Address, phone, CTA buttons */}
      <VenueInfoCard venueDetails={venueDetails} openInGoogleMaps={openInGoogleMaps} />

                {/* Venue About Section */}
                {venueDetails && (
                  <div className="venue-description-section">
                    <h2>About This Place</h2>
                    <div className="venue-description">
                      {venueDetails.description || "No description available for this venue."}
                    </div>

                    {/* Secondary Actions */}
                    <div className="venue-secondary-actions">
                      {venueDetails.website && (
                        <a
                          href={venueDetails.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="venue-cta-btn secondary"
                        >
                          <FaGlobe /> Visit Website
                        </a>
                      )}
                      <button
                        onClick={handleShareVenue}
                        className="venue-cta-btn share-btn"
                        aria-label="Share this venue"
                      >
                        <FaShare /> Share
                      </button>
                    </div>
                  </div>
                )}

                {/* Menu Section */}
                {venueDetails && (
                  <div className="venue-info-card">
                    <h2>Menu</h2>
                    {venueDetails.menu_link ? (
                      <div className="venue-cta-buttons">
                        <a
                          href={venueDetails.menu_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="venue-cta-btn view-menu-btn"
                        >
                          View Full Menu <FaExternalLinkAlt />
                        </a>
                      </div>
                    ) : (
                      <p className="venue-info-placeholder">No menu available for this venue.</p>
                    )}
                  </div>
                )}

                {/* Reviews Section */}
                {venueDetails && (
                  <div className="venue-info-card">
                    <h2>Top Review</h2>
                    {venueDetails.review_sample ? (
                      <div className="venue-review-sample">
                        <p className="review-text">"{venueDetails.review_sample}"</p>
                      </div>
                    ) : (
                      <p className="venue-info-placeholder">No reviews available for this venue.</p>
                    )}
          </div>
        )}
    </>
  );

  // Right column content
  const rightColumn = (
    <VenueCouponsSection
      ref={couponsRef}
      coupons={coupons}
      setCoupons={setCoupons}
      wishlistCoupons={wishlistCoupons}
      savingCouponId={savingCouponId}
      handleWishlistToggle={handleWishlistToggle}
      isPublicMode={isPublicMode}
      customerToken={customerToken}
      apiUrl={apiUrl}
      venueId={venueId}
      isInterested={isInterested}
      venueHasActiveOffers={venueHasActiveOffers}
      handleInterestToggle={handleInterestToggle}
      interestedLoading={interestedLoading}
      triggerLogin={triggerLogin}
      showToast={showToast}
      updateWishlistCount={updateWishlistCount}
      getCustomerWishlist={getCustomerWishlist}
      error={error}
      onViewOtherVenues={() => navigate(appendCompanyCode("/perks-directory"))}
    />
  );

  // Mobile floating content
  const mobileFloatingContent = isMobile && (
    <div className={`go-to-coupons-container ${couponsSectionVisible || isAtBottom ? '' : 'show'}`} >
      <div className="go-to-coupons-details">
        {readyCoupons.length > 0 ? (
          <>
            <span className="go-to-title">
              {readyCoupons.at(0).title.toUpperCase()}
            </span>
            {readyCoupons.at(0).frequency === 'always_on' ? (
              <>
                <br />
                <span className="go-to-exp">
                  No expiration date
                </span>
              </>
            ) : readyCoupons.at(0).expires_at && (
              <>
                <br />
                <span className="go-to-exp">
                  Use by {new Date(readyCoupons.at(0).expires_at).toLocaleDateString()}
                </span>
              </>
            )}
          </>
        ) : (
          <span className="go-to-no-coupons">
            No Active Coupons.
            <br />
            Check Back Next Week!
          </span>
        )}
      </div>
      <button
        className="go-to-coupons-btn"
        onClick={() => {
          if (readyCoupons.length > 0) {
            couponsRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          } else {
            navigate(appendCompanyCode("/perks-directory"));
          }
        }}
      >
        <span className="btn-text-desktop">
          {readyCoupons.length > 0 ? 'Go to Coupons' : 'View Other Venues'}
        </span>
        <span className="btn-text-mobile">
          {readyCoupons.length > 0 ? 'Coupons' : 'Other Venues'}
        </span>
      </button>
    </div>
  );

  return (
    <>
      <div className="coupon-page">
        {/* Main Content with DetailPageLayout */}
        <main className="coupon container">
          <DetailPageLayout
            heroGalleryUrls={venueDetails?.gallery_urls}
            heroFallbackImage={venueDetails?.poster_url}
            heroSecondaryFallback={venueDetails?.logo_url}
            heroTertiaryFallback={
              // Only use BC logo if venue has no gallery, no poster, and no logo
              (!venueDetails?.gallery_urls || venueDetails.gallery_urls.length === 0) &&
              !venueDetails?.poster_url &&
              !venueDetails?.logo_url
                ? bcLogo
                : null
            }
            heroAlt={venueDetails?.venue_name}
            heroOverlayContent={heroOverlay}
            leftColumnContent={leftColumn}
            rightColumnContent={rightColumn}
            rightColumnRef={couponsRef}
            mobileFloatingContent={mobileFloatingContent}
            containerClassName="page-layout"
            mainWrapperClassName="venue-detail-main-wrapper"
            leftColumnClassName="venue-detail-content-column"
            rightColumnClassName="coupons-main-section"
          />
        </main>

        {/* Toast Notification */}
        {toast && (
          <div className={`toast toast-${toast.type}`}>
            <div className="toast-content">
              <span className="toast-message">{toast.message}</span>
            </div>
          </div>
        )}
      </div >
    </>
  )
}
